import React, { useState } from 'react';
import axios from 'axios';
import { CheckCircle, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Apply = () => {
  const [activeForm, setActiveForm] = useState('school'); // 'school' or 'coaching'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitError, setSubmitError] = useState('');

  const [schoolForm, setSchoolForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    program: 'actor-training',
    experience: '',
    motivation: '',
    commitment: ''
  });

  const [coachingForm, setCoachingForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    location: '',
    background: '',
    currentPractice: '',
    reason: '',
    duration: ''
  });

  const handleSchoolChange = (e) => {
    setSchoolForm({ ...schoolForm, [e.target.name]: e.target.value });
  };

  const handleCoachingChange = (e) => {
    setCoachingForm({ ...coachingForm, [e.target.name]: e.target.value });
  };

  const handleSchoolSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError('');
    
    try {
      // Convert camelCase to snake_case for backend
      const payload = {
        full_name: schoolForm.fullName,
        email: schoolForm.email,
        phone: schoolForm.phone,
        location: schoolForm.location,
        program: schoolForm.program,
        experience: schoolForm.experience,
        motivation: schoolForm.motivation,
        commitment: schoolForm.commitment
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/applications/school`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      setSubmitMessage(response.data.message);
      setSubmitSuccess(true);
      
      // Reset form
      setSchoolForm({
        fullName: '',
        email: '',
        phone: '',
        location: '',
        program: 'actor-training',
        experience: '',
        motivation: '',
        commitment: ''
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        setSubmitError(error.response.data.detail);
      } else {
        setSubmitError('An error occurred while submitting your application. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCoachingSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError('');
    
    try {
      // Convert camelCase to snake_case for backend
      const payload = {
        full_name: coachingForm.fullName,
        email: coachingForm.email,
        phone: coachingForm.phone,
        location: coachingForm.location,
        background: coachingForm.background,
        current_practice: coachingForm.currentPractice,
        reason: coachingForm.reason,
        duration: coachingForm.duration
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/applications/coaching`,
        payload,
        { headers: { 'Content-Type': 'application/json' } }
      );
      
      setSubmitMessage(response.data.message);
      setSubmitSuccess(true);
      
      // Reset form
      setCoachingForm({
        fullName: '',
        email: '',
        phone: '',
        location: '',
        background: '',
        currentPractice: '',
        reason: '',
        duration: ''
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      if (error.response && error.response.data && error.response.data.detail) {
        setSubmitError(error.response.data.detail);
      } else {
        setSubmitError('An error occurred while submitting your request. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-32">
      {/* Hero */}
      <section className="py-20">
        <div className="container-custom">
          <h1 className="heading-1 mb-8">APPLY</h1>
          <p className="body-large max-w-3xl">
            Submit your application for the GPS Coaching School or request private coaching.
          </p>
        </div>
      </section>

      {/* Form Selector */}
      <section className="py-12 bg-card">
        <div className="container-custom">
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setActiveForm('school')}
              className={activeForm === 'school' ? 'btn-primary' : 'btn-secondary'}
            >
              Apply to School
            </button>
            <button
              onClick={() => setActiveForm('coaching')}
              className={activeForm === 'coaching' ? 'btn-primary' : 'btn-secondary'}
            >
              Request Coaching
            </button>
          </div>
        </div>
      </section>

      {/* Success Message */}
      {submitSuccess && (
        <section className="py-8">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto bg-card border border-primary p-6 rounded-sm">
              <div className="flex items-start gap-4">
                <CheckCircle size={24} className="text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="heading-6 mb-2 text-primary">Submission Received</h3>
                  <p className="body-small text-secondary">{submitMessage}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Error Message */}
      {submitError && (
        <section className="py-8">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto bg-card border border-red-500 p-6 rounded-sm">
              <div className="flex items-start gap-4">
                <div className="text-red-500 flex-shrink-0 mt-1">⚠</div>
                <div>
                  <h3 className="heading-6 mb-2 text-red-500">Submission Error</h3>
                  <p className="body-small text-secondary">{submitError}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* School Application Form */}
      {activeForm === 'school' && (
        <section className="py-20">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto">
              <h2 className="heading-2 mb-8">COACHING SCHOOL APPLICATION</h2>
              <form onSubmit={handleSchoolSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={schoolForm.fullName}
                    onChange={handleSchoolChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={schoolForm.email}
                    onChange={handleSchoolChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={schoolForm.phone}
                    onChange={handleSchoolChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label htmlFor="location" className="form-label">Current Location *</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={schoolForm.location}
                    onChange={handleSchoolChange}
                    required
                    className="form-input"
                    placeholder="City, Country"
                  />
                </div>

                <div>
                  <label htmlFor="program" className="form-label">Program of Interest *</label>
                  <select
                    id="program"
                    name="program"
                    value={schoolForm.program}
                    onChange={handleSchoolChange}
                    required
                    className="form-input"
                  >
                    <option value="one-week-intensive">One-Week Intensive Course (1 week)</option>
                    <option value="two-month-actor">Two-Month Actor Training (2 months)</option>
                    <option value="actor-training">Extended Actor Training (12 months)</option>
                    <option value="coach-development">Coach Development (18 months)</option>
                    <option value="advanced-labs">Advanced Labs (Ongoing)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="experience" className="form-label">
                    Relevant Experience *
                  </label>
                  <textarea
                    id="experience"
                    name="experience"
                    value={schoolForm.experience}
                    onChange={handleSchoolChange}
                    required
                    rows={4}
                    className="form-input"
                    placeholder="Describe your background in acting, coaching, or related fields"
                  />
                </div>

                <div>
                  <label htmlFor="motivation" className="form-label">
                    Why the GPS Method? *
                  </label>
                  <textarea
                    id="motivation"
                    name="motivation"
                    value={schoolForm.motivation}
                    onChange={handleSchoolChange}
                    required
                    rows={4}
                    className="form-input"
                    placeholder="What draws you to this method specifically?"
                  />
                </div>

                <div>
                  <label htmlFor="commitment" className="form-label">
                    Commitment Statement *
                  </label>
                  <textarea
                    id="commitment"
                    name="commitment"
                    value={schoolForm.commitment}
                    onChange={handleSchoolChange}
                    required
                    rows={4}
                    className="form-input"
                    placeholder="This training requires significant time and dedication. Explain your readiness to commit."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Private Coaching Form */}
      {activeForm === 'coaching' && (
        <section className="py-20">
          <div className="container-custom">
            <div className="max-w-2xl mx-auto">
              <h2 className="heading-2 mb-8">PRIVATE COACHING REQUEST</h2>
              <form onSubmit={handleCoachingSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={coachingForm.fullName}
                    onChange={handleCoachingChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={coachingForm.email}
                    onChange={handleCoachingChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={coachingForm.phone}
                    onChange={handleCoachingChange}
                    required
                    className="form-input"
                  />
                </div>

                <div>
                  <label htmlFor="location" className="form-label">Current Location *</label>
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={coachingForm.location}
                    onChange={handleCoachingChange}
                    required
                    className="form-input"
                    placeholder="City, Country"
                  />
                </div>

                <div>
                  <label htmlFor="background" className="form-label">
                    Professional Background *
                  </label>
                  <textarea
                    id="background"
                    name="background"
                    value={coachingForm.background}
                    onChange={handleCoachingChange}
                    required
                    rows={4}
                    className="form-input"
                    placeholder="Describe your professional background and current work"
                  />
                </div>

                <div>
                  <label htmlFor="currentPractice" className="form-label">
                    Current Practice *
                  </label>
                  <textarea
                    id="currentPractice"
                    name="currentPractice"
                    value={coachingForm.currentPractice}
                    onChange={handleCoachingChange}
                    required
                    rows={4}
                    className="form-input"
                    placeholder="What is your current practice or training approach?"
                  />
                </div>

                <div>
                  <label htmlFor="reason" className="form-label">
                    Why Seek Private Coaching? *
                  </label>
                  <textarea
                    id="reason"
                    name="reason"
                    value={coachingForm.reason}
                    onChange={handleCoachingChange}
                    required
                    rows={4}
                    className="form-input"
                    placeholder="What specific work do you need support with?"
                  />
                </div>

                <div>
                  <label htmlFor="duration" className="form-label">
                    Intended Duration *
                  </label>
                  <select
                    id="duration"
                    name="duration"
                    value={coachingForm.duration}
                    onChange={handleCoachingChange}
                    required
                    className="form-input"
                  >
                    <option value="">Select duration</option>
                    <option value="3-months">3 Months</option>
                    <option value="6-months">6 Months</option>
                    <option value="ongoing">Ongoing</option>
                  </select>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={20} />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Note */}
      <section className="py-20 bg-card">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="body-small text-secondary">
              All applications are reviewed carefully. We will respond within 5-7 business days.
              Acceptance is not guaranteed and is based on readiness, alignment, and availability.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Apply;